import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { manufacturingApi } from '../api/manufacturing';
import { productsApi } from '../api/products';
import { FeatureGate } from '../components/FeatureGate';

const fmt = (n?: number) =>
  n !== undefined ? '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';

const fmtQty = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });

interface ComponentRow {
  componentProductId: string;
  qty: number;
  unit: string;
  wastagePercent: number;
}

export default function BomEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';

  const [productId, setProductId] = useState('');
  const [version, setVersion] = useState('1.0');
  const [notes, setNotes] = useState('');
  const [directLabourPerUnit, setDirectLabourPerUnit] = useState(0);
  const [directOverheadPerUnit, setDirectOverheadPerUnit] = useState(0);
  const [components, setComponents] = useState<ComponentRow[]>([{ componentProductId: '', qty: 1, unit: 'pcs', wastagePercent: 0 }]);
  const [error, setError] = useState<string | null>(null);
  const [costQty, setCostQty] = useState(1);
  const [costResult, setCostResult] = useState<any>(null);

  const { data: products = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => productsApi.list(undefined, 1, 500).then((r) => r.data),
    staleTime: 60_000,
  });

  // Soft filter: category-matching products are grouped first, but nothing is excluded —
  // uncategorized products (and the opposite category, e.g. a finished good used as a
  // sub-assembly component) still show, just lower in the list.
  const finishedGoodProducts = products.filter((p: any) => p.category === 'finished_good');
  const otherFinishedProductOptions = products.filter((p: any) => p.category !== 'finished_good');
  const rawMaterialProducts = products.filter((p: any) => p.category === 'raw_material');
  const otherComponentOptions = products.filter((p: any) => p.category !== 'raw_material');

  const { data: bom, isLoading: bomLoading } = useQuery({
    queryKey: ['bom', id],
    queryFn: () => manufacturingApi.getBom(id!),
    enabled: !isNew && !!id,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (bom) {
      setProductId(bom.productId);
      setVersion(bom.version);
      setNotes(bom.notes ?? '');
      setDirectLabourPerUnit(Number(bom.directLabourPerUnit ?? 0));
      setDirectOverheadPerUnit(Number(bom.directOverheadPerUnit ?? 0));
      setComponents(
        bom.components.map((comp) => ({
          componentProductId: comp.componentProductId,
          qty: Number(comp.qty),
          unit: comp.unit ?? comp.component?.unit ?? 'pcs',
          wastagePercent: Number(comp.wastagePercent ?? 0),
        })),
      );
    }
  }, [bom]);

  const saveBom = useMutation({
    mutationFn: () => {
      const payload = { productId, version, notes, directLabourPerUnit, directOverheadPerUnit, components };
      if (isNew) return manufacturingApi.createBom(payload as any);
      return manufacturingApi.updateBom(id!, payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boms'] }); qc.refetchQueries({ type: 'all' }); navigate('/manufacturing/boms'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? e.message),
  });

  const deactivateBom = useMutation({
    mutationFn: () => manufacturingApi.updateBom(id!, { isActive: false } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boms'] }); qc.refetchQueries({ type: 'all' }); navigate('/manufacturing/boms'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? e.message),
  });

  const calcCost = useMutation({
    mutationFn: () => manufacturingApi.calcBomCost(id!, costQty),
    onSuccess: (data) => setCostResult(data),
    onError: (e: any) => setError(e?.response?.data?.message ?? e.message),
  });

  const addRow = () =>
    setComponents((rows) => [...rows, { componentProductId: '', qty: 1, unit: 'pcs', wastagePercent: 0 }]);

  const removeRow = (i: number) => setComponents((rows) => rows.filter((_, idx) => idx !== i));

  const updateRow = (i: number, field: keyof ComponentRow, value: string | number) =>
    setComponents((rows) =>
      rows.map((r, idx) => {
        if (idx !== i) return r;
        const updated = { ...r, [field]: value };
        if (field === 'componentProductId') {
          const p = products.find((pr: any) => pr.id === value);
          if (p) updated.unit = p.unit ?? 'pcs';
        }
        return updated;
      }),
    );

  return (
    <FeatureGate feature="manufacturingModule" requiredPlan="Gold" upgradeMessage="Manufacturing module requires a Gold plan.">
      <div style={{ padding: '1.5rem', maxWidth: '800px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/manufacturing/boms')}>
            ← Back
          </button>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600 }}>
            {isNew ? 'New Bill of Materials' : `Edit BOM — ${bom?.product?.name ?? '…'}`}
          </h2>
        </div>

        {bomLoading && <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>}

        {error && (
          <div style={{ padding: '0.75rem', fontSize: '0.875rem' }} className="error-box">
            {error}
          </div>
        )}

        {/* Header fields */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
          <div className="form-row form-row-2" style={{ gap: '0.75rem', marginBottom: 0 }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Finished Product *</label>
              <select
                value={productId}
                disabled={!isNew}
                onChange={(e) => setProductId(e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
              >
                <option value="">— Select finished product —</option>
                {finishedGoodProducts.length > 0 && (
                  <optgroup label="Finished Goods">
                    {finishedGoodProducts.map((p: any) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Other Products">
                  {otherFinishedProductOptions.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Version</label>
              <input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
              />
            </div>
          </div>
          <div className="form-row form-row-2" style={{ gap: '0.75rem', marginTop: '0.75rem', marginBottom: 0 }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Direct Labour / Unit (₹)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={directLabourPerUnit}
                onChange={(e) => setDirectLabourPerUnit(Number(e.target.value) || 0)}
                style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Direct Overhead / Unit (₹)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={directOverheadPerUnit}
                onChange={(e) => setDirectOverheadPerUnit(Number(e.target.value) || 0)}
                style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Components table */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Components</h3>
            <button className="btn btn-secondary btn-sm" onClick={addRow}>+ Add Row</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 500 }}>Component</th>
                <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 500, width: '100px' }}>Qty</th>
                <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 500, width: '80px' }}>Unit</th>
                <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 500, width: '100px' }}>Wastage %</th>
                <th style={{ width: '40px' }} />
              </tr>
            </thead>
            <tbody>
              {components.map((row, i) => {
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      <select
                        value={row.componentProductId}
                        onChange={(e) => updateRow(i, 'componentProductId', e.target.value)}
                        style={{ width: '100%', padding: '0.3rem 0.5rem', border: '1px solid var(--border-input)', borderRadius: '4px', fontSize: '0.825rem' }}
                      >
                        <option value="">— Select —</option>
                        {rawMaterialProducts.length > 0 && (
                          <optgroup label="Raw Materials">
                            {rawMaterialProducts.map((p: any) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="Other Products">
                          {otherComponentOptions.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </optgroup>
                      </select>
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={row.qty}
                        onChange={(e) => updateRow(i, 'qty', Number(e.target.value))}
                        style={{ width: '100%', padding: '0.3rem 0.5rem', border: '1px solid var(--border-input)', borderRadius: '4px', fontSize: '0.825rem', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      <input
                        value={row.unit}
                        onChange={(e) => updateRow(i, 'unit', e.target.value)}
                        style={{ width: '100%', padding: '0.3rem 0.5rem', border: '1px solid var(--border-input)', borderRadius: '4px', fontSize: '0.825rem' }}
                      />
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={row.wastagePercent}
                        onChange={(e) => updateRow(i, 'wastagePercent', Number(e.target.value))}
                        style={{ width: '100%', padding: '0.3rem 0.5rem', border: '1px solid var(--border-input)', borderRadius: '4px', fontSize: '0.825rem', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                      <button
                        onClick={() => removeRow(i)}
                        style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {components.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0', margin: 0 }}>
              No components added yet.
            </p>
          )}
        </div>

        {/* Cost calculator (only for saved BOMs) */}
        {!isNew && (
          <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 600 }}>Manufacturing Cost Calculator</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Production Qty:</label>
              <input
                type="number"
                min="1"
                value={costQty}
                onChange={(e) => setCostQty(Number(e.target.value))}
                style={{ width: '80px', padding: '0.3rem 0.5rem', border: '1px solid var(--border-input)', borderRadius: '4px', fontSize: '0.875rem' }}
              />
              <button className="btn btn-secondary btn-sm" onClick={() => calcCost.mutate()} disabled={calcCost.isPending}>
                Calculate
              </button>
            </div>

            {costResult && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                <div className="form-row form-row-2" style={{ gap: '0.5rem', marginBottom: 0 }}>
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Total Material Cost</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text)' }}>
                      {fmt(costResult.cost?.totalMaterialCost)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>for {fmtQty(costQty)} units</div>
                  </div>
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Direct Labour Cost</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text)' }}>
                      {fmt(costResult.cost?.directLabourCost)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>for {fmtQty(costQty)} units</div>
                  </div>
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Direct Overhead Cost</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text)' }}>
                      {fmt(costResult.cost?.directOverheadCost)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>for {fmtQty(costQty)} units</div>
                  </div>
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Total Manufacturing Cost</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text)' }}>
                      {fmt(costResult.cost?.totalCost)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>for {fmtQty(costQty)} units</div>
                  </div>
                  <div style={{ background: 'var(--primary-light)', border: '1px solid var(--primary)', borderRadius: '6px', padding: '0.75rem', gridColumn: '1 / -1' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Cost Per Unit</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text)' }}>
                      {fmt(costResult.cost?.costPerUnit)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>material + labour + overhead</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            {!isNew && (
              <button
                className="btn btn-secondary btn-sm"
                style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={() => { if (window.confirm('Deactivate this BOM?')) deactivateBom.mutate(); }}
                disabled={deactivateBom.isPending}
              >
                Deactivate BOM
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/manufacturing/boms')}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!productId || components.length === 0 || saveBom.isPending}
              onClick={() => saveBom.mutate()}
            >
              {saveBom.isPending ? 'Saving…' : isNew ? 'Create BOM' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
}
